import React, { Fragment, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'

import styles from './Transcripts.module.css'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Button from 'Design/components/Button'
import FormErrorText from 'Design/components/FormErrorText'
import HelpText from 'Design/components/HelpText'
import Link from 'Design/components/Link'
import SwitchInput from 'Design/components/SwitchInput'

import { useResendTranscriptVerificationEmailMutation, useUpdateTranscriptEmailMutation } from 'Services/bot-profiles'

import { useBotSettingsContext } from '../SettingsContext'
import transcriptFormValidationSchema from './transcript-form-validation-schema'

function getTranscriptEmail(transcriptEmailSettings) {
  let email = ''

  if (transcriptEmailSettings) {
    email = transcriptEmailSettings.email

    if (transcriptEmailSettings.email_change_request?.email) {
      email = transcriptEmailSettings.email_change_request?.email
    }
  }

  return email
}

const Transcripts = () => {
  const bot = useBotSettingsContext()

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty, isSubmitSuccessful },
    reset,
    control,
    getValues,
    watch
  } = useForm({
    defaultValues: {
      email: getTranscriptEmail(bot.settings?.transcript_email),
      enableAnonymousTranscript: bot.settings?.transcript_email?.enable_anonymous_transcript ?? false
    },
    resolver: yupResolver(transcriptFormValidationSchema)
  })

  const email = watch('email')

  const [updateTranscript, transcriptResult] = useUpdateTranscriptEmailMutation()
  const [resentVerificationEmail, resentVerificationEmailResult] = useResendTranscriptVerificationEmailMutation()

  const onFormSubmit = (data) => {
    const payload = {
      id: bot.id,
      email: data.email,
      enable_anonymous_transcript: data.enableAnonymousTranscript
    }

    const promise = updateTranscript(payload).unwrap()

    toast.promise(promise, {
      loading: 'Updating transcript settings',
      success: 'Transcript settings updated successfully',
      error: 'Error while updating transcript settings. Try again...'
    })

    return promise
  }

  const disableSubmitButton =
    isSubmitting || transcriptResult.isLoading || resentVerificationEmailResult.isLoading || !isDirty
  const isEmailChangeRequestExists = bot.settings?.transcript_email?.email_change_request?.email
  const disableSwitchInput = !email

  const handleResendVerificationEmail = () => {
    const promise = resentVerificationEmail(bot.id).unwrap()

    toast.promise(promise, {
      loading: 'Resending verification email',
      success: 'Verification email sent successfully',
      error: 'Error while sending verification email. Try again...'
    })
  }

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return
    }

    reset({ ...getValues() })
  }, [isSubmitSuccessful, reset, getValues])

  return (
    <Fragment>
      <div className={styles.title}>Bot Transcripts</div>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <FormControl isInvalid={errors.email}>
          <Label>You can forward all transcripts from your organization to an e-mail address</Label>
          <Input {...register('email')} autoComplete='off' />
          {isEmailChangeRequestExists && (
            <HelpText>
              Verify the above email address to receive transcripts.{' '}
              <Link onClick={handleResendVerificationEmail} disabled={resentVerificationEmailResult.isLoading}>
                Resend Verification Email
              </Link>
            </HelpText>
          )}
          {errors.email && <FormErrorText>{errors.email.message}</FormErrorText>}
        </FormControl>

        <FormControl>
          <Controller
            name='enableAnonymousTranscript'
            control={control}
            render={({ field: { value, onChange } }) => {
              if (!email) {
                value = false
              }
              return (
                <SwitchInput checked={value} onChange={onChange} disabled={disableSwitchInput}>
                  Include transcripts that do not have visitor email
                </SwitchInput>
              )
            }}
          />
        </FormControl>

        <Button type='submit' variant='primary' disabled={disableSubmitButton}>
          Save
        </Button>
      </form>
    </Fragment>
  )
}

export default Transcripts
